import { Link } from "@tanstack/react-router";
import type { PublicTeam } from "@/lib/types";

/**
 * `TeamCard` — public-grid card for `/teams` (M12a).
 *
 * Brand kit consumer: every color/spacing routes through tokens
 * (`bg-surface`, `border-rule`, `text-ink`, etc.) so the card
 * inherits light + dark theme parity from `tokens.css`.
 *
 * The card surfaces the slim public-team payload: name, slug,
 * match count, and the earliest upcoming match date when one
 * exists. Per-team logos / brand colors / live badges are deferred
 * — the Team schema doesn't carry those columns yet (deferred per
 * the M12 parity doc; see "Out of scope for M12a").
 */
export function TeamCard({ team }: { team: PublicTeam }) {
  return (
    <Link
      to="/teams/$teamSlug"
      params={{ teamSlug: team.slug }}
      data-testid="team-card"
      data-team-slug={team.slug}
      className="group flex flex-col gap-3 rounded-lg border border-rule bg-surface p-5 transition-colors hover:border-accent"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-lg leading-snug tracking-tight text-ink group-hover:text-accent">
          {team.name}
        </h3>
        <span className="rounded border border-rule px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted">
          {team.game_format}
        </span>
      </div>
      <dl className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-ink-muted">
        <div className="flex items-center gap-1.5">
          <dt className="uppercase tracking-wider">Matches</dt>
          <dd
            data-testid="team-card-match-count"
            className="font-display text-ink"
          >
            {team.match_count}
          </dd>
        </div>
        {team.next_match_at ? (
          <div className="flex items-center gap-1.5">
            <dt className="uppercase tracking-wider">Next</dt>
            <dd data-testid="team-card-next-at" className="text-ink">
              {team.next_match_at}
            </dd>
          </div>
        ) : null}
      </dl>
      <span
        aria-hidden="true"
        className="mt-auto font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted group-hover:text-ink"
      >
        open team →
      </span>
    </Link>
  );
}
